import {
  Button,
  ButtonStyledButton,
  Card as DefaultCard,
  Label as DefaultLabel,
  List as DefaultList, ListItem,
  PriceBlock as DefaultPriceBlock,
  Space
} from '@codex-brightworld/basics';
import { Switchable } from '@codex-brightworld/gadgets';
import { Icon } from '@codex-brightworld/icons';
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 ${props => props.theme.spacing[1]};

  @media ${props => props.theme.breakpoints.mobile} {
    flex-direction: column;
  }
`;

const CardWrapper = styled.div`
  flex-grow: 1;
  padding: 0 ${props => props.theme.spacing[2]};

  @media ${props => props.theme.breakpoints.mobile} {
    &:not(:first-child) {
      padding-top: ${props => props.theme.spacing[2]};
    }
  }
`;

const Card = styled(DefaultCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: 0;
  max-width: 800px;

  ${props => props.isActive && css`
    border-color: ${props.theme.colors.positive.default};
  `};

  .dc-card__title {
    height: 37px;
  };

  .dc-card__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

const List = styled(DefaultList)`
  flex: 1 1 auto;
  font-size: 12px;
  padding-bottom: 2px;

  @media ${props => props.theme.breakpoints.mobile} {
    padding-left: ${props => props.theme.spacing[2]};
    padding-right: ${props => props.theme.spacing[2]};
  }

  ${ListItem} {
    margin-bottom: 7px;

    ${Icon} {
      color: ${props => props.theme.colors.positive.default}
    }
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: ${props => (props.isSingle ? 'row' : 'column')};
  flex-grow: 1;
  justify-content: space-between;
  padding: ${props => props.theme.spacing[2]};

  ${List} {
    ${props => props.isSingle && css`
      width: 50%;
      padding: 0 ${props.theme.spacing[2]};
    `};
  }

  @media ${props => props.theme.breakpoints.mobile} {
    flex-direction: column;

    ${List} {
      width: 100%;
      padding: 0;
    }
  }
`;

const SingleItemInnerWrapper = styled.div`
  width: 50%;
  padding-left: 25px;
  padding-right: ${props => props.theme.spacing[2]};

  @media ${props => props.theme.breakpoints.mobile} {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
  }

  ${ButtonStyledButton} {
    width: 100%;
    max-width: initial;
  }
`;

const PriceBlock = styled(DefaultPriceBlock)``;

const Label = styled(DefaultLabel)``;

class CardSelection extends PureComponent {

  renderCard(index, card, currentCard, switchCard, cardsCount) {
    const { props } = this;
    const isActive = card.id === currentCard;
    const isSingle = cardsCount === 1;
    const InnerWrapper = isSingle ? SingleItemInnerWrapper : Fragment;

    return (
      <CardWrapper key={index}>
        <Card title={card.title} isActive={isActive}>
          <CardContent isSingle={isSingle}>
            <List items={card.features} />
            <InnerWrapper>
              <PriceBlock price={card.price} info={card.priceInfo} />
              {card.label && (
                <Space all="10 0">
                  <Label title={card.label} subTitle={card.subLabel} />
                </Space>
              )}
              <Space top={10}>
                <Button
                  secondary
                  onClick={() => switchCard(isActive ? undefined : card.id)}
                >
                  {isActive ? props.removeText : props.addText}
                </Button>
              </Space>
            </InnerWrapper>
          </CardContent>
        </Card>
      </CardWrapper>
    );
  }

  render() {
    const { props } = this;

    return (
      <Wrapper className={props.className} data-qa={props.qa}>
        <Switchable current={props.defaultCardId}>
          {({ current, switchTo }) => props.cards.map(
            (card, index) => this.renderCard(index, card, current, switchTo, props.cards.length)
          )}
        </Switchable>
      </Wrapper>
    );
  }

}

CardSelection.displayName = 'CardSelection';

CardSelection.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      features: PropTypes.arrayOf(PropTypes.string),
      id: PropTypes.string,
      price: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object
      ]),
      priceInfo: PropTypes.string,
      label: PropTypes.string,
      subLabel: PropTypes.string
    })
  ).isRequired,
  defaultCardId: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  subLabel: PropTypes.string,
  addText: PropTypes.string,
  removeText: PropTypes.string,
  qa: PropTypes.string
};

CardSelection.defaultProps = {
  className: undefined,
  defaultCardId: undefined,
  label: '',
  subLabel: '',
  addText: 'Add',
  removeText: 'Remove',
  qa: undefined
};

export default CardSelection;
